import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { SupportTicket, getTicketStatusColor, getTicketPriorityColor } from './shared'

export const SupportTickets: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['support-module-tickets', page, search, statusFilter, priorityFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(limit))
      if (search) params.append('search', search)
      if (statusFilter) params.append('status', statusFilter)
      if (priorityFilter) params.append('priority', priorityFilter)
      const response = await api.get(`/support/tickets?${params.toString()}`)
      return response.data.data
    },
  })

  const tickets: SupportTicket[] = data?.tickets || []
  const total: number = data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const getStatusLabel = (status: string) =>
    t(`supportModule.status${status.charAt(0).toUpperCase()}${status.slice(1)}`)

  const getPriorityLabel = (priority: string) =>
    t(`supportModule.priority${priority.charAt(0).toUpperCase()}${priority.slice(1)}`)

  return (
    <div>
      {/* Filters */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder={t('supportModule.searchTickets')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="flex-1 px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          >
            <option value="">{t('supportModule.allStatus')}</option>
            <option value="open">{t('supportModule.statusOpen')}</option>
            <option value="pending">{t('supportModule.statusPending')}</option>
            <option value="resolved">{t('supportModule.statusResolved')}</option>
            <option value="closed">{t('supportModule.statusClosed')}</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          >
            <option value="">{t('supportModule.allPriority')}</option>
            <option value="low">{t('supportModule.priorityLow')}</option>
            <option value="medium">{t('supportModule.priorityMedium')}</option>
            <option value="high">{t('supportModule.priorityHigh')}</option>
            <option value="urgent">{t('supportModule.priorityUrgent')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">{t('supportModule.noTicketsTitle')}</p>
            <p className="text-sm">{t('supportModule.noTicketsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.reference')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.subject')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.customer')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.status')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.priority')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.assignedTo')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => navigate(`/support-module/tickets/${ticket.id}`)}
                    >
                      <td className="px-4 py-3 text-[#006cd4] font-mono text-sm">{ticket.reference}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium max-w-xs truncate">{ticket.subject}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {ticket.customer_name || ticket.customer_email || t('supportModule.anonymous')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTicketStatusColor(ticket.status)}`}>
                          {getStatusLabel(ticket.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTicketPriorityColor(ticket.priority)}`}>
                          {getPriorityLabel(ticket.priority)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {ticket.assigned_to_name || t('supportModule.unassigned')}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('common.showingOf', { count: tickets.length, total })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('common.pageOf', { page, total: totalPages })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
